import { useState } from "react";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getActualsMovements } from "@/utils/budgetApi";

const eur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n ?? 0);

const isZero = (n) => Math.abs(n ?? 0) < 0.005;
// Tono para celdas de partida: atenúa los ceros para que las cifras reales salten.
const cellTone = (n, base) => (isZero(n) ? "text-slate-300" : base);
// Tono para "Resultado del mes" / "En el banco": rojo en negativo, atenuado en cero.
const resultTone = (n) => {
  if (n == null) return "text-slate-400";
  if (isZero(n)) return "text-slate-300";
  return n < 0 ? "text-red-700" : "text-slate-800";
};

const MONTH_NAMES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function monthLabel(mo) {
  if (!mo) return "";
  const m = parseInt(mo.slice(5, 7), 10);
  return `${MONTH_NAMES[m - 1] ?? mo} ${mo.slice(2, 4)}`;
}

// Rejilla de SOLO LECTURA para el histórico real (mismo formato que el presupuesto).
// Acepta props `start`/`end` para el drill-down de movimientos.
export default function BudgetGridReadOnly({ budget, computed, start, end }) {
  const [dialog, setDialog] = useState({ open: false, title: "", rows: [], loading: false, error: null });

  const openDrillDown = async (line) => {
    setDialog({ open: true, title: line.name, rows: [], loading: true, error: null });
    try {
      const data = await getActualsMovements(start, end, line.kind, line.name);
      setDialog((prev) => ({ ...prev, rows: data.movements ?? [], loading: false }));
    } catch {
      setDialog((prev) => ({ ...prev, loading: false, error: "No se pudieron cargar los movimientos." }));
    }
  };

  const closeDialog = () => setDialog((prev) => ({ ...prev, open: false }));

  const scenario = computed?.scenarios?.medium;
  if (!budget || !scenario?.months?.length) {
    return <p className="text-gray-400 mb-8">No hay movimientos en este rango.</p>;
  }
  const months = scenario.months.map((m) => m.month);
  const byMonth = Object.fromEntries(scenario.months.map((m) => [m.month, m]));
  const lines = budget.lines ?? [];
  const income = lines.filter((l) => l.kind === "income");
  const expense = lines.filter((l) => l.kind === "expense");
  const lineTotal = (l) => months.reduce((a, mo) => a + (l.monthly_amounts?.[mo] ?? 0), 0);

  const renderRows = (group, title, amountColor) => (
    <>
      <tr className="border-t bg-slate-100">
        <td colSpan={months.length + 2} className="px-3 py-2 font-semibold text-slate-700">{title}</td>
      </tr>
      {group.map((l) => (
        <tr key={l.id} className="border-b last:border-0 hover:bg-slate-50">
          <td className="sticky left-0 z-10 bg-white px-3 py-1.5 text-sm">
            {start && end ? (
              <button
                onClick={() => openDrillDown(l)}
                className="group inline-flex items-center gap-1.5 text-left text-slate-700 underline decoration-dotted decoration-slate-300 underline-offset-2 hover:text-blue-700 hover:decoration-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                title={`Ver movimientos de ${l.name}`}
              >
                {l.name}
                <Search className="w-3 h-3 text-slate-300 group-hover:text-blue-600 transition-colors" />
              </button>
            ) : (
              l.name
            )}
          </td>
          {months.map((mo) => {
            const v = l.monthly_amounts?.[mo] ?? 0;
            return (
              <td key={mo} className={`text-right px-2 py-1.5 tabular-nums text-sm ${cellTone(v, amountColor)}`}>
                {eur(v)}
              </td>
            );
          })}
          <td className={`text-right px-3 py-1.5 tabular-nums font-medium ${cellTone(lineTotal(l), amountColor)}`}>{eur(lineTotal(l))}</td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="border rounded-lg bg-white overflow-x-auto mb-8">
        <table className="text-sm border-collapse min-w-full">
          <thead>
            <tr className="text-gray-500 border-b bg-slate-50">
              <th className="sticky left-0 z-10 bg-slate-50 text-left font-medium px-3 py-2 min-w-[240px]">Partida</th>
              {months.map((mo) => (
                <th key={mo} className="text-right font-medium px-2 py-2 min-w-[90px] whitespace-nowrap">{monthLabel(mo)}</th>
              ))}
              <th className="text-right font-semibold px-3 py-2 min-w-[100px]">Total</th>
            </tr>
          </thead>
          <tbody>
            {renderRows(income, "Lo que entra", "text-green-700")}
            {renderRows(expense, "Lo que sale", "text-slate-700")}

            <tr className="border-t-2 border-slate-300 font-semibold">
              <td className="sticky left-0 z-10 bg-white px-3 py-2">Resultado del mes</td>
              {months.map((mo) => {
                const r = byMonth[mo]?.result;
                return (
                  <td key={mo} className={`text-right px-2 py-2 tabular-nums ${resultTone(r)}`}>
                    {r != null ? eur(r) : "—"}
                  </td>
                );
              })}
              <td className="text-right px-3 py-2 tabular-nums">{eur(scenario.totals?.net)}</td>
            </tr>
            <tr className="font-bold bg-slate-100 border-t border-slate-300">
              <td className="sticky left-0 z-10 bg-slate-100 px-3 py-2.5">En el banco</td>
              {months.map((mo) => {
                const c = byMonth[mo]?.cash;
                return (
                  <td key={mo} className={`text-right px-2 py-2.5 tabular-nums ${resultTone(c)}`}>
                    {c != null ? eur(c) : "—"}
                  </td>
                );
              })}
              <td className="text-right px-3 py-2.5 tabular-nums">{eur(scenario.totals?.cash_final)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <Dialog open={dialog.open} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Movimientos — {dialog.title}</DialogTitle>
          </DialogHeader>
          {dialog.loading && <p className="text-gray-400 text-sm py-4">Cargando…</p>}
          {dialog.error && <p className="text-red-600 text-sm py-4">{dialog.error}</p>}
          {!dialog.loading && !dialog.error && dialog.rows.length === 0 && (
            <p className="text-gray-400 text-sm py-4">Sin movimientos en este rango.</p>
          )}
          {!dialog.loading && !dialog.error && dialog.rows.length > 0 && (
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="text-sm border-collapse w-full">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b text-gray-500">
                    <th className="text-left font-medium px-2 py-2 min-w-[90px]">Fecha</th>
                    <th className="text-right font-medium px-2 py-2 min-w-[100px]">Importe</th>
                    <th className="text-left font-medium px-2 py-2">Descripción</th>
                    <th className="text-left font-medium px-2 py-2 min-w-[140px]">Proveedor</th>
                  </tr>
                </thead>
                <tbody>
                  {dialog.rows.map((m, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-2 py-1.5 tabular-nums text-gray-600 whitespace-nowrap">{m.date}</td>
                      <td className={`text-right px-2 py-1.5 tabular-nums font-medium ${m.amount < 0 ? "text-slate-700" : "text-green-700"}`}>
                        {eur(Math.abs(m.amount))}
                      </td>
                      <td className="px-2 py-1.5 text-gray-700 max-w-[240px] truncate" title={m.description ?? ""}>{m.description ?? "—"}</td>
                      <td className="px-2 py-1.5 text-gray-600 truncate" title={m.supplier ?? ""}>{m.supplier ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
